export type AnnualPolicyPrintDocument = Readonly<{
  year: string;
  vision: string;
  goal: string;
  effective: string;
  targets: readonly Readonly<{ site: string; item: string; value: string | number; unit: string }>[];
  principles: readonly string[];
}>;

type FlowUnit = {
  text: string;
  render(text: string, continued: boolean): HTMLElement;
};

const logoUrl = '/brand/kop-main-horizontal.png';

/** Original vector ornament echoes the company's connected navy/red forms.
 * It is decorative artwork, separate from both the official logo and document text.
 */
function brandArtwork(variant: 'header' | 'sheet') {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', variant === 'header' ? '0 0 320 230' : '0 0 794 1123');
  svg.setAttribute('class', `policy-${variant}-art`);
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('focusable', 'false');
  const shapes = variant === 'header' ? [
    ['policy-art-soft', 'M7 15H226C272 15 309 52 309 98V220H288V98C288 64 260 36 226 36H7Z'],
    ['policy-art-navy', 'M19 66H211C240 66 263 89 263 118S240 170 211 170H149V134H211C220 134 227 127 227 118S220 102 211 102H19Z'],
    ['policy-art-red', 'M301 199H109C80 199 57 176 57 147S80 95 109 95H171V131H109C100 131 93 138 93 147S100 163 109 163H301Z'],
  ] : [
    ['policy-art-soft', 'M794 0V300C773 286 751 274 732 256V0Z'],
    ['policy-art-navy', 'M0 1088C128 1088 163 1123 310 1123H0Z'],
    ['policy-art-red', 'M0 1108C98 1108 133 1123 220 1123H118C75 1116 46 1115 0 1115Z'],
  ];
  for (const [className, outline] of shapes) {
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('class', className!);
    path.setAttribute('d', outline!);
    svg.append(path);
  }
  return svg;
}

function element<K extends keyof HTMLElementTagNameMap>(tag: K, className: string, text?: string) {
  const node = document.createElement(tag);
  node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

function heading(title: string, detail?: string) {
  const node = element('div', 'policy-section-heading');
  node.append(element('h2', '', title));
  if (detail) node.append(element('span', '', detail));
  return node;
}

function textUnit(kind: 'vision' | 'goal' | 'principle', text: string, index = 0): FlowUnit {
  return { text, render(chunk, continued) {
    const unit = element('section', `policy-unit policy-${kind}`);
    unit.dataset.policyField = kind === 'principle' ? `principle-${index}` : kind;
    if (kind === 'vision' || kind === 'goal') {
      unit.append(element('div', 'policy-unit-label', `${kind === 'vision' ? '안전보건 경영비전' : '안전보건 경영목표'}${continued ? ' · 계속' : ''}`));
    } else {
      if (index === 0 && !continued) unit.append(heading('안전보건 경영방침'));
      unit.append(element('span', 'policy-principle-number', `${String(index + 1).padStart(2, '0')}${continued ? ' · 계속' : ''}`));
    }
    unit.append(element('p', 'policy-value', chunk || '미작성'));
    return unit;
  } };
}

function flowUnits(data: AnnualPolicyPrintDocument): FlowUnit[] {
  const units = [textUnit('vision', data.vision), textUnit('goal', data.goal)];
  for (let index = 0; index < data.targets.length; index += 4) {
    const rows = data.targets.slice(index, index + 4);
    const text = rows.map(row => `${row.site}\n${row.value} ${row.unit}\n${row.item}`).join('\n\n');
    units.push({ text, render(chunk, continued) {
      const section = element('section', 'policy-unit policy-target-section');
      section.dataset.policyField = `targets-${index}`;
      if (index === 0 && !continued) section.append(heading('사업장별 목표', `${data.year}년 연간 계획`));
      if (chunk === text && !continued) {
        const grid = element('div', 'policy-targets');
        grid.style.gridTemplateColumns = `repeat(${rows.length}, minmax(0, 1fr))`;
        for (const row of rows) {
          const target = element('div', 'policy-target');
          const number = element('div', 'policy-target-number');
          number.append(element('strong', '', String(row.value)), element('span', '', row.unit));
          target.append(element('h3', '', row.site), number, element('p', '', row.item));
          grid.append(target);
        }
        section.append(grid);
      } else {
        // Unusually long individual target text keeps its full content on continuation pages.
        section.append(element('p', 'policy-value policy-target-continuation', chunk));
      }
      return section;
    } });
  }
  data.principles.forEach((text, index) => units.push(textUnit('principle', text, index)));
  return units;
}

async function page(container: HTMLElement, data: AnnualPolicyPrintDocument, note: string, index: number) {
  const sheet = element('article', `policy-sheet${index ? ' policy-sheet-continued' : ''}`);
  sheet.setAttribute('aria-label', `안전보건 목표·경영방침 ${index + 1}쪽`);
  const header = element('header', 'policy-header');
  const brand = element('div', 'policy-brand-row');
  const logo = element('img', 'policy-logo');
  logo.src = logoUrl;
  logo.alt = '한국오션플랫폼';
  const edition = element('div', 'policy-edition');
  edition.append(element('strong', '', `${data.year}년`), element('span', '', '안전보건 경영방침'));
  brand.append(logo, edition);
  const title = element('h1', '');
  if (index) title.textContent = '안전보건 목표 및 경영방침 · 계속';
  else title.append(element('span', '', '안전보건 목표'), element('span', 'policy-title-secondary', '및 경영방침'));
  header.append(brand, title);
  if (!index) header.append(brandArtwork('header'));
  const body = element('div', 'policy-page-body');
  const footer = element('footer', 'policy-footer');
  const notice = element('div', 'policy-footer-note');
  notice.append(element('strong', '', '한국오션플랫폼(주)'), element('span', '', note));
  const meta = element('div', 'policy-footer-meta');
  meta.append(element('span', '', `적용일 ${data.effective || '미작성'}`), element('span', 'policy-page-number'));
  footer.append(notice, meta);
  sheet.append(brandArtwork('sheet'), header, body, footer);
  container.append(sheet);
  await logo.decode();
  return body;
}

function fits(body: HTMLElement) {
  const bounds = body.getBoundingClientRect();
  const last = body.lastElementChild?.getBoundingClientRect();
  return !last || last.bottom <= bounds.bottom + 0.25;
}

/** One rendering tree is shared by the editable preview and the browser's PDF/print output.
 * The caller must pass an already-authorized document revision, never substitute a newer draft.
 * This renderer has no persistence, role, approval or publication authority.
 */
export async function renderAnnualPolicyPrint(
  container: HTMLElement,
  input: AnnualPolicyPrintDocument,
  options: Readonly<{ note: string }>,
): Promise<Readonly<{ pageCount: number }>> {
  const data = structuredClone(input);
  container.replaceChildren();
  container.classList.add('annual-policy-pages');
  container.dataset.ready = 'false';
  await container.ownerDocument.fonts.ready;
  let pageIndex = 0;
  let body = await page(container, data, options.note, pageIndex);
  const nextPage = async () => { body = await page(container, data, options.note, ++pageIndex); };
  for (const unit of flowUnits(data)) {
    if (!container.isConnected) throw new Error('출력 미리보기가 닫혔습니다.');
    let node = unit.render(unit.text, false);
    body.append(node);
    if (fits(body)) continue;
    node.remove();
    if (body.childElementCount > 0) {
      const candidate = await page(container, data, options.note, pageIndex + 1);
      candidate.append(node);
      if (fits(candidate)) { body = candidate; pageIndex++; continue; }
      node.remove();
      candidate.parentElement?.remove();
    }
    const characters = Array.from(unit.text);
    let offset = 0;
    while (offset < characters.length) {
      if (!container.isConnected) throw new Error('출력 미리보기가 닫혔습니다.');
      let low = 1, high = characters.length - offset, best = 0;
      while (low <= high) {
        const count = Math.floor((low + high) / 2);
        node = unit.render(characters.slice(offset, offset + count).join(''), offset > 0);
        body.append(node);
        const accepted = fits(body);
        node.remove();
        if (accepted) { best = count; low = count + 1; } else high = count - 1;
      }
      if (best === 0) {
        if (body.childElementCount > 0) { await nextPage(); continue; }
        throw new Error('출력 양식의 크기를 확인해 주세요.');
      }
      if (best < characters.length - offset) {
        const space = characters.slice(offset, offset + best).lastIndexOf(' ');
        if (space >= Math.floor(best * .8)) best = space + 1;
      }
      body.append(unit.render(characters.slice(offset, offset + best).join(''), offset > 0));
      offset += best;
      if (offset < characters.length) await nextPage();
    }
  }
  const sheets = [...container.querySelectorAll<HTMLElement>('.policy-sheet')];
  sheets.forEach((sheet, index) => {
    const count = sheet.querySelector('.policy-page-number');
    if (count) count.textContent = `${index + 1} / ${sheets.length}`;
  });
  container.dataset.ready = 'true';
  return { pageCount: sheets.length };
}
